import { ReactNode, useState, useEffect } from 'react'
import { Sidebar } from './Sidebar'

interface LayoutProps {
  children: ReactNode
}

export const Layout = ({ children }: LayoutProps) => {
  const [sidebarState, setSidebarState] = useState<'expanded' | 'collapsed' | 'hidden'>('expanded')
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth
      if (width >= 1024) {
        // Desktop: always show expanded sidebar
        setSidebarState('expanded')
        setIsMobileMenuOpen(false)
      } else if (width >= 768) {
        // Tablet: show only icons
        setSidebarState('collapsed')
        setIsMobileMenuOpen(false)
      } else {
        // Mobile: hide sidebar, use burger menu
        setSidebarState('hidden')
      }
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const toggleSidebar = () => {
    if (window.innerWidth < 768) {
      // Mobile: toggle mobile menu
      setIsMobileMenuOpen(!isMobileMenuOpen)
    } else {
      // Desktop/Tablet: toggle between expanded and collapsed
      setSidebarState(prev => prev === 'expanded' ? 'collapsed' : 'expanded')
    }
  }

  const getMainContentClass = () => {
    const baseClass = 'min-h-screen transition-all duration-300'
    
    if (sidebarState === 'hidden') {
      return `${baseClass} pl-0` // Mobile: no padding
    } else if (sidebarState === 'collapsed') {
      return `${baseClass} pl-16` // Tablet: icon-only sidebar
    } else {
      return `${baseClass} pl-64` // Desktop: full sidebar
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Sidebar 
        state={sidebarState}
        isVisible={sidebarState !== 'hidden'}
        isMobileMenuOpen={isMobileMenuOpen}
        onMobileClose={() => setIsMobileMenuOpen(false)}
      />
      
      {/* Main content area */}
      <div className={getMainContentClass()}>
        {/* Mobile burger menu button */}
        {sidebarState === 'hidden' && (
          <div className="sticky top-0 z-30 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between px-4 py-3">
              <button
                onClick={toggleSidebar}
                className="p-2 rounded-md text-gray-500 hover:text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-700"
                aria-label="Ouvrir le menu"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="text-lg font-medium text-gray-900 dark:text-white">
                SkeenFit BO
              </h1>
              <div className="w-10" /> {/* Spacer for centering */}
            </div>
          </div>
        )}
        
        <main className="p-6">
          {children}
        </main>
      </div>
      
      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
          onKeyDown={(e) => e.key === 'Escape' && setIsMobileMenuOpen(false)}
          role="button"
          tabIndex={0}
          aria-label="Close mobile menu"
        />
      )}
    </div>
  )
}